import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "Which type of support in the MSPSS model emphasizes shared responsibility and long-term commitment?",
    options: [
      "Significant Other support",
      "Family support",
      "Friends support",
      "Professional support"
    ],
    correctAnswer: 1,
    explanation: "Family support in the MSPSS model focuses on shared responsibility, guidance, belonging, and long-term commitment that comes from familial relationships."
  },
  {
    question: "What is the key difference between received and perceived social support?",
    options: [
      "Received support is more important than perceived support",
      "Perceived support is the actual help you get, while received support is what you believe is available",
      "Received support is tangible help provided, while perceived support is your belief that help is available",
      "They are essentially the same thing"
    ],
    correctAnswer: 2,
    explanation: "Received support refers to actual, tangible help and assistance you get from others, while perceived support is your subjective belief that support is available if you need it."
  },
  {
    question: "According to research, social support can reduce depression risk by what percentage range?",
    options: [
      "10-20%",
      "20-30%",
      "40-60%",
      "70-80%"
    ],
    correctAnswer: 2,
    explanation: "Studies show that strong social support networks can reduce the risk of depression by 40-60%, demonstrating the significant protective effects of social connections."
  },
  {
    question: "In collectivistic cultures, social support is typically expressed through:",
    options: [
      "Verbal expressions of emotion",
      "Direct confrontation and problem-solving",
      "Actions, presence, and group harmony",
      "Individual achievement and independence"
    ],
    correctAnswer: 2,
    explanation: "In collectivistic cultures, support is often expressed through actions, physical presence, and maintaining group harmony, rather than verbal emotional expressions."
  },
  {
    question: "Which hormone is released during positive social interactions and contributes to bonding?",
    options: [
      "Cortisol",
      "Adrenaline",
      "Oxytocin",
      "Insulin"
    ],
    correctAnswer: 2,
    explanation: "Oxytocin, often called the 'bonding hormone,' is released during positive social interactions and helps strengthen social bonds and trust."
  }
];

const SocioRelationalModule2QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('socioRelationalM2');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 2 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>The science of social support</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Different types of support networks</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Cultural aspects of social connections</span>
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your socio-relational wellness journey by assessing your support network!</p>
            <p>Ready to evaluate your personal relationships and connections?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/socio-relational-resources')}
          >
            Return to Socio-Relational Resources
          </button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left socio-relational-panel">
          <div className="module-header">
            <h1>Module 2 Quiz</h1>
            <p className="module-subtitle">Building Healthy Relationships</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/socio-relational-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocioRelationalModule2QuizPage;

